// src/components/base/Flex/Flex.tsx
import { ComponentChildren } from 'preact';
// styles
import styles from './Flex.module.css';

export type Spacing =
  | 'xxxs'
  | 'xxs'
  | 'xs'
  | 'sm'
  | 'base'
  | 'md'
  | 'lg'
  | 'xl';

interface FlexProps {
  className?: string;
  children?: ComponentChildren;

  direction?: 'row' | 'column';
  wrap?: 'nowrap' | 'wrap' | 'wrap-reverse';
  justify?: 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';
  align?: 'start' | 'center' | 'end' | 'stretch' | 'baseline';
  gap?: Spacing;

  m?: Spacing;
  mt?: Spacing;
  mb?: Spacing;
  ml?: Spacing;
  mr?: Spacing;

  p?: Spacing;
  pt?: Spacing;
  pb?: Spacing;
  pl?: Spacing;
  pr?: Spacing;
}

function space(v?: Spacing) {
  return v ? `var(--internal-spacing-${v})` : undefined;
}

function cx(...classes: Array<string | false | undefined>) {
  return classes.filter(Boolean).join(' ');
}

/**
 * A Flex container based on Chakra UI’s Flex, using a simple utility API.
 * @param className - The class name to apply to the flex container.
 * @param children - The children to render inside the flex container.
 * @param direction - The direction of the flex container.
 * @param wrap - The wrap of the flex container.
 * @param justify - The justify of the flex container.
 * @param align - The align of the flex container.
 * @param gap - The gap of the flex container.
 * @param rest - The rest of the props to apply to the flex container.
 * @returns
 */
export function Flex({
  className,
  children,

  direction,
  wrap,
  justify,
  align,
  gap,

  m,
  mt,
  mb,
  ml,
  mr,

  p,
  pt,
  pb,
  pl,
  pr,
}: FlexProps) {
  const combinedClasses = cx(
    styles.flex,

    direction && styles[direction],
    wrap && styles[wrap],
    justify && styles[`justify-${justify}`],
    align && styles[`align-${align}`],

    className,
  );

  const style: Record<string, string | undefined> = {
    gap: space(gap),

    margin: space(m),
    marginTop: space(mt),
    marginBottom: space(mb),
    marginLeft: space(ml),
    marginRight: space(mr),

    padding: space(p),
    paddingTop: space(pt),
    paddingBottom: space(pb),
    paddingLeft: space(pl),
    paddingRight: space(pr),
  };

  return (
    <div className={combinedClasses} style={style}>
      {children}
    </div>
  );
}
